import React from 'react';
import { Typography } from '@interstate/components/Typography';
import { Spinner } from '@interstate/components/Spinner';
import type { VIEW_MODE } from '../CustomerCardEnums';
import { CustomerAddressForm } from './CustomerAddressForm';
import { CustomerAddressCard } from './CustomerAddressCard';
import { AddressCardHeader } from './AddressCardHeader';
import { useCustomerAddress } from './CustomerAddressProvider';
import { CardList, CardContainer, LoadingIndicatorWrapper } from './CustomerAddresses.styles';
import { Button } from '@interstate/components/Button';
import { PlusIcon } from '@interstate/components/Icons/PlusIcon';
import { BuyerAddressCopy } from './BuyerAddressCopy';
import type { BUYER } from '../constants';
import { CO_BUYER } from '../constants';

export type CustomerAddressProps = {
    customer: typeof BUYER | typeof CO_BUYER;
    viewMode: VIEW_MODE;
    disabled?: boolean;
};

export const CustomerAddresses = ({ customer, viewMode, disabled }: CustomerAddressProps) => {
    const { addresses, isLoading, handleAddressFieldChanged, formValidations, handleAddBillingAddress } = useCustomerAddress();

    const isViewMode = addresses.every((address) => address.mode === 'view');
    const isBillingAddressPresent = !!addresses.find((address) => address.type === 'billing');

    return (
        <div>
            <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                Addresses
            </Typography>

            {customer === CO_BUYER && <BuyerAddressCopy />}

            {isLoading && isViewMode ? (
                <LoadingIndicatorWrapper data-testid="address-card-loader" hasMultipleAddresses={addresses.length > 1}>
                    <Spinner />
                </LoadingIndicatorWrapper>
            ) : (
                <CardList>
                    {addresses.map((address) => {
                        if (address.mode === 'view') {
                            return <CustomerAddressCard key={address.type} type={address.type} />;
                        } else {
                            return (
                                <CardContainer key={address.type} isAddressCard header={<AddressCardHeader type={address.type} />}>
                                    <CustomerAddressForm
                                        formErrors={formValidations[address.type]?.formErrors}
                                        values={address.data}
                                        handleInputChange={(field, value) => handleAddressFieldChanged(address.type, field, value)}
                                        viewMode={viewMode}
                                        disabled={disabled}
                                    />
                                </CardContainer>
                            );
                        }
                    })}
                </CardList>
            )}

            {!isBillingAddressPresent && (
                <Button
                    sx={{ marginTop: '1rem', width: '100%' }}
                    startIcon={<PlusIcon />}
                    buttonStyle="secondary"
                    size="small"
                    onClick={() => handleAddBillingAddress()}
                    disabled={disabled}
                >
                    Add Billing Address
                </Button>
            )}
        </div>
    );
};
